import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPatch } from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency';
import { useVatMode } from '../hooks/useVatMode';
import { BTN, BTN_XS, BTN_LG } from '../constants/buttons';
import { LISTING_STATUS_COLORS, LISTING_STATUS_LABELS } from '../constants/colors';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import EmptyState from '../components/EmptyState';
import HireForgeEarlyNotice from '../components/HireForgeEarlyNotice';

function MarketplaceMyListings() {
  const navigate = useNavigate();
  const { formatMoney } = useVatMode();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiGet('/api/marketplace/my-listings');
      if (!res.ok) throw new Error('Failed to load listings');
      const data = await res.json();
      setListings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const toggleExpanded = (id) => {
    if (expandedId === id) {
      // Collapse with animation
      setClosingId(id);
      setTimeout(() => {
        setExpandedId(null);
        setClosingId(null);
      }, 250);
    } else {
      // Close any currently open
      if (expandedId) {
        setClosingId(expandedId);
        setTimeout(() => {
          setClosingId(null);
        }, 250);
      }
      // Open new
      setExpandedId(id);
      setOpeningId(id);
      setTimeout(() => setOpeningId(null), 50);
    }
  };

  const handleStatusChange = async (listingId, newStatus) => {
    if (newStatus === 'removed') {
      if (!window.confirm('Are you sure you want to remove this listing? It will no longer be visible.')) return;
    }
    try {
      setActionLoading(listingId);
      const res = await apiPatch(`/api/marketplace/listings/${listingId}/status`, { status: newStatus });
      if (!res.ok) throw new Error('Failed to update listing');
      setListings(prev =>
        prev.map(l => l.id === listingId ? { ...l, status: newStatus } : l)
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const formatPrice = (listing) => {
    if (listing.is_poa) {
      return (
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          POA
        </span>
      );
    }
    // 🚨 `!= null` AND NOT `&& > 0` — a £0 rate is a PRICE (owner, 2026-09-20):
    // a free or loaned item is a real offer, and POA above is already the
    // separate "ask me" state. The truthiness gate showed "No price" over a
    // rate the server had stored correctly, and showed it for the NUMBER `0`
    // while letting the STRING `'0'` past its first half — the two halves of
    // one listing's own data disagreeing. 🔑 Only `null` means no price now.
    // Pinned across all three marketplace surfaces by `MarketplaceZeroRate.test.js`.
    if (listing.daily_rate != null) {
      return <span className="text-sm font-medium text-gray-900 dark:text-white">{formatMoney(listing.daily_rate)}/day</span>;
    }
    if (listing.weekly_rate != null) {
      return <span className="text-sm font-medium text-gray-900 dark:text-white">{formatMoney(listing.weekly_rate)}/wk</span>;
    }
    return <span className="text-xs text-gray-400 dark:text-gray-500">No price</span>;
  };

  if (loading) return <LoadingSpinner message="Loading your listings..." />;
  if (error) return <ErrorMessage error={error} />;

  return (
    <div className="max-w-4xl mx-auto p-2 md:p-4 lg:p-6">
      {/* Early-version notice — see components/HireForgeEarlyNotice.js. It sits
          inside the loaded branch, after the two early returns above: while the
          spinner is up there is nothing on screen to be misled by, and a
          notice that flashed before the page would be read as a page-load
          error. */}
      <HireForgeEarlyNotice className="mb-4" />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Listings</h1>
        <Link to="/hireforge/listings/new" className={BTN_LG.primary}>
          Add New Listing
        </Link>
      </div>

      {/* Empty state */}
      {listings.length === 0 && (
        <EmptyState
          size="md"
          title="You haven't listed anything yet"
          description="List a piece of kit or a service to make it discoverable to other productions."
          cta={{ label: 'Add Your First Listing', to: '/hireforge/listings/new' }}
        />
      )}

      {/* Listing cards */}
      <div className="space-y-3">
        {listings.map((listing) => {
          const isExpanded = expandedId === listing.id;
          const isClosing = closingId === listing.id;
          const isOpening = openingId === listing.id;
          const thumbnail = listing.primary_image;

          return (
            <div key={listing.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Collapsed card row */}
              <button
                type="button"
                onClick={() => toggleExpanded(listing.id)}
                className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                  isExpanded
                    ? 'bg-gray-100 dark:bg-slate-700'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                }`}
              >
                {/* Thumbnail */}
                {thumbnail ? (
                  <img
                    src={thumbnail}
                    alt=""
                    className="w-12 h-12 rounded object-cover flex-shrink-0 bg-gray-100 dark:bg-gray-700"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                    </svg>
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{listing.title}</p>
                  {listing.category_name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{listing.category_name}</p>
                  )}
                </div>

                {/* Price */}
                <div className="flex-shrink-0 text-right">
                  {formatPrice(listing)}
                </div>

                {/* Status badge */}
                <span className={`flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${LISTING_STATUS_COLORS[listing.status] || ''}`}>
                  {LISTING_STATUS_LABELS[listing.status] || listing.status}
                </span>

                {/* Chevron */}
                <svg
                  className={`w-5 h-5 flex-shrink-0 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Expanded section */}
              {(isExpanded || isClosing) && (
                <div
                  className={`border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 p-4 transition-opacity duration-200 ease-out ${
                    isClosing || isOpening ? 'opacity-0' : 'opacity-100'
                  }`}
                >
                  {/* Description */}
                  {listing.description && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Description</h3>
                      <p className="whitespace-pre-line text-sm text-gray-700 dark:text-gray-300">{listing.description}</p>
                    </div>
                  )}

                  {/* Location */}
                  {listing.location && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Location</h3>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{listing.location}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {listing.notes && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Notes</h3>
                      <p className="whitespace-pre-line text-sm text-gray-700 dark:text-gray-300">{listing.notes}</p>
                    </div>
                  )}

                  {/* Availability */}
                  {listing.availability && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Availability</h3>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{listing.availability}</p>
                    </div>
                  )}

                  {/* Images grid */}
                  {listing.images && listing.images.length > 0 && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Photos</h3>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                        {listing.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img.file_path}
                            alt={`${listing.title} ${idx + 1}`}
                            className="w-full aspect-square object-cover rounded-md bg-gray-100 dark:bg-gray-700"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap mt-3">
                    <button
                      className={BTN.primary}
                      onClick={() => navigate(`/hireforge/listings/${listing.id}/edit`)}
                    >
                      Edit
                    </button>
                    {listing.status === 'paused' ? (
                      <button
                        className={BTN.confirm}
                        onClick={() => handleStatusChange(listing.id, 'active')}
                        disabled={actionLoading === listing.id}
                      >
                        {actionLoading === listing.id ? 'Updating...' : 'Unpause'}
                      </button>
                    ) : listing.status === 'active' ? (
                      <button
                        className={BTN.warning}
                        onClick={() => handleStatusChange(listing.id, 'paused')}
                        disabled={actionLoading === listing.id}
                      >
                        {actionLoading === listing.id ? 'Updating...' : 'Pause'}
                      </button>
                    ) : null}
                    {listing.status !== 'removed' && (
                      <button
                        className={BTN_XS.danger}
                        onClick={() => handleStatusChange(listing.id, 'removed')}
                        disabled={actionLoading === listing.id}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MarketplaceMyListings;
